/**
 * i18n-aware relative-time formatter.
 *
 * Produces short, translated "time ago" strings (just now / Ns ago / Nm ago /
 * Nh ago / Nd ago) using the `app.*` keys in common.json. Use this instead of
 * hand-rolling relative-time strings in components — those bypass i18n and show
 * English in non-English locales.
 *
 * For full date-fns relative formatting (e.g. activity feeds with "about 2
 * hours ago"), pass a locale to date-fns instead; this helper covers the
 * compact indicator case.
 *
 * NOTE: the keys are unprefixed (`app.justNow`, not `common:app.justNow`), so
 * the `t` you pass MUST be scoped to the `common` namespace
 * (`useTranslation('common')`). Callers in another namespace should pass their
 * `common`-scoped `t` (e.g. `const { t: tCommon } = useTranslation('common')`).
 */
import type { TFunction } from 'i18next';

export function formatRelativeTime(
  t: TFunction,
  date: Date,
  now: Date = new Date()
): string {
  const diffSec = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 1000));
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 10) {
    return t('app.justNow');
  }
  if (diffSec < 60) {
    return t('app.secondsAgo', { count: diffSec });
  }
  if (diffMin < 60) {
    return t('app.minutesAgo', { count: diffMin });
  }
  if (diffHour < 24) {
    return t('app.hoursAgo', { count: diffHour });
  }
  return t('app.daysAgo', { count: diffDay });
}
